import { Column } from '@nirvana/shared/dtos/types/shared/filters';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { GridBaseDataProvider } from '../../../grid/GridBaseDataProvider';
import { GetRowsRequest, GetSummaryRequest } from '../../../grid/types/common';
import { sanitizeObject } from '../../../utils/sanitization.utils';
import { API_TYPE_ACCOUNTS } from '../../../webcomponents.constants';
import {
  AdditionalFilterProps,
  AdditionalFilters,
  EntityRole,
  NetworkColumn,
  NetworkGridItem,
  NetworkReadRecordsParams,
  PaginationType,
  ProtectedEntitiesNetwork,
} from '../helpers/types';
import { convertGridFiltersToExtJsFilters } from '../../../grid/utils/filters';
import { ExtJsFilter } from '../../../grid/utils/filters/types';
import { Filter } from '../../../grid/gridFilters/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { getAbsoluteTimerangeFromRelativeInterval } from '../../../utils/filters.utils';
import { getTimezone } from '../../../utils/global.utils';
import { jsTimestampToUnixTimestamp } from '@nirvana/shared/time';
import { partition } from 'lodash';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { getViewTypeFromType } from '../../networkTree/helpers/tree.helper';
import { AdditionalFiltersHelper } from './additional.filters.helper';
import { Feature } from '@nirvana/shared/constants';
import { Subject } from 'rxjs';

export class NetworkGridDataProvider extends GridBaseDataProvider<Column, NetworkColumn> {
  public companyFilterDataProvider = API_TYPE_ACCOUNTS;

  private getCountPromise: Promise<number> | undefined;
  private countPromiseFunctions: {
    resolve: Function;
    reject: Function;
  };
  private skipNextLoad = false;
  public error$ = new Subject<any>();

  private gridLabelToFilterableProperty = new Map([
    ['name', 'name'],
    ['fqdn', 'specifics.fqdn'],
    ['ip', 'specifics.ip'],
    ['label', 'specifics.label'],
    ['endpointType', 'specifics.isVirtualServer'],
    ['osVersion', 'specifics.operatingSystemVersion'],
    ['osType', 'specifics.operatingSystem'],
    ['containerId', 'specifics.uuid'],
    ['containerName', 'specifics.imageName'],
    ['users', 'specifics.endpointUsers.userNames'],
    ['lastSeen', 'specifics.lastSync'],
    ['company', 'company.id'],
    ['tags', 'gzTags'],
    ['policy', 'specifics.clientPolicy.policyId'],
  ]);

  private sortDirections = ['ASC', 'DESC']; // only these sort directions are accepted

  private gridLabelToSortableProperty = new Map([
    ['name', 'name'],
    ['company', 'company.name'],
    ['osVersion', 'specifics.operatingSystemVersion'],
    ['ip', 'specifics.ipBinary'],
    ['lastSeen', 'specifics.lastSync'],
    ['fqdn', 'specifics.fqdn'],
    ['label', 'specifics.label'],
    ['policy', 'specifics.clientPolicy.policyName'],
    ['containerName', 'specifics.imageName'],
    ['containerId', 'specifics.uuid'],
  ]);

  private keysOfAdditionalFilters = [
    NetworkColumn.TYPE,
    NetworkColumn.MANAGEMENT_STATUS,
    NetworkColumn.SECURITY_ISSUES,
    NetworkColumn.PENDING_ACTIVITY,
    NetworkColumn.ROLE,
    NetworkColumn.POLICY_STATUS,
    NetworkColumn.POLICY_TYPE,
    NetworkColumn.POWER,
    NetworkColumn.AWS_INTEGRATION_TAGS,
    NetworkColumn.AVAILABLE_POLICIES,
    NetworkColumn.IN_DELETED_FOLDER,
    NetworkColumn.MIGRATED_TO_ANOTHER_COMPANY,
  ];

  private getEndpointTypesToAdd(filters: Array<Filter<NetworkColumn>>): Array<string> {
    const endpointFilterKeys = [
      NetworkColumn.OS_TYPE,
      NetworkColumn.OS_VERSION,
      NetworkColumn.MANAGEMENT_STATUS,
      NetworkColumn.ROLE,
      NetworkColumn.PENDING_ACTIVITY,
      NetworkColumn.MIGRATED_TO_ANOTHER_COMPANY,
      NetworkColumn.IN_DELETED_FOLDER,
      NetworkColumn.LABEL,
      NetworkColumn.FQDN,
      NetworkColumn.USERS,
      NetworkColumn.IP,
      NetworkColumn.SECURITY_ISSUES,
      NetworkColumn.LAST_SEEN,
      NetworkColumn.ENDPOINT_TYPE,
    ];
    const containerFilterKeys = [NetworkColumn.CONTAINER_ID, NetworkColumn.CONTAINER_NAME];

    const allEndpointFilterKeys = [...endpointFilterKeys, ...containerFilterKeys];
    // We're using this function because it also removes empty filters
    const extFilters: ExtJsFilter<NetworkColumn>[] = convertGridFiltersToExtJsFilters(filters);
    const queryFilters = extFilters.filter(this.shouldKeepFilterInQuery);

    const endpointFilters = queryFilters.filter(filter => {
      return allEndpointFilterKeys.includes(filter.property);
    });

    if (!endpointFilters.length) {
      return [];
    }

    // If role filter exists, there is no need to enforce anything, because this filter has type filter set on backend
    const roleFilter = endpointFilters.find(filter => filter.property === NetworkColumn.ROLE);
    if (roleFilter) {
      return [];
    }

    // If type filter exists and it has a value, we don't want to add endpoint types
    const typeFilter = filters.find(filter => !!filter[NetworkColumn.TYPE]);
    if (!!typeFilter?.type?.in?.length) {
      return [];
    }

    const hasContainerVisible = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_CONTAINER_PROTECTION_VISIBLE]);
    // If we only have container filters, we want to only add the container type
    if (!endpointFilters.some(filter => endpointFilterKeys.includes(filter.property))) {
      if (hasContainerVisible) {
        return [
          Constants.Network.Filters.Types.CONTAINERS, // Container
        ];
      }

      return [];
    }

    const computersFilters = [
      Constants.Network.Filters.Types.COMPUTERS, // Physical machine
      Constants.Network.Filters.Types.VIRTUAL_MACHINES, // Virtual machine
    ];

    const hasEc2Configured = Application.Main.hasFeatures([
      Constants.Features.PRODUCT_FEATURE_AMAZON_EC2_CLOUD_INTEGRATION_CONFIGURED,
    ]);
    if (hasEc2Configured) {
      computersFilters.push(Constants.Network.Filters.Types.EC2_INSTANCES);
    }

    if (hasContainerVisible) {
      return [
        ...computersFilters,
        Constants.Network.Filters.Types.CONTAINERS, // Container
      ];
    }

    return computersFilters;
  }

  //@ts-ignore
  async getSummary(request: GetSummaryRequest) {
    if (!this.shouldContinueRequest(false)) {
      return {
        summary: '',
        numItems: 0,
      };
    }

    const numItems = await this.getCountPromise;
    delete this.getCountPromise;

    return {
      summary: '',
      numItems: numItems,
    };
  }

  async getRows(request: GetRowsRequest<Column, NetworkColumn>) {
    if (!request.pageSize || !request.pageNumber) {
      throw Error('No page size sent for grid request!');
    }

    if (!this.shouldContinueRequest(true)) {
      return {
        rawEvents: [],
      };
    }

    const entityTypeFiltersToAdd = this.getEndpointTypesToAdd(request.filters);
    if (entityTypeFiltersToAdd.length !== 0) {
      // remove old entity type filter, and add the new one
      request.filters = request.filters.filter(filter => !filter.type);
      request.filters.push({
        [NetworkColumn.TYPE]: {
          in: entityTypeFiltersToAdd,
        },
      });
    }

    const { additionalFiltersFromGrid, gridFilters } = this.transformGridFiltersForBackend(request.filters);
    const depth = request.extraParams.isRecursive
      ? Constants.ProtectedEntities.EPS.FILTERS.DEPTH.SHOW_ALL_ITEMS_RECURSIVELY
      : Constants.ProtectedEntities.EPS.FILTERS.DEPTH.SHOW_ITEMS_WITHIN_THE_SELECTED_FOLDERS;

    const additionalFilters = this.buildAdditionalFilters({
      depth: depth,
      additionalFiltersFromGrid,
    });

    const nodeId = request.extraParams.nodeId;
    const type = request.extraParams.type;

    const payload: NetworkReadRecordsParams = {
      page: request.extraParams.ignoreLimit ? 1 : request.pageNumber,
      limit: request.extraParams.ignoreLimit ? 1000 : request.pageSize, // better safe than sorry
      viewType: getViewTypeFromType(type),
      nodeId,
      additionalFilters,
      filter: gridFilters,
      paginationType: request.extraParams.ignoreLimit
        ? PaginationType.PAGINATION_TYPE_OPTIMIZED
        : PaginationType.PAGINATION_TYPE_DEFAULT,
    };

    if (this.shouldAddSortToRequest(request)) {
      payload.dir = request.sortDirection;
      payload.sort = this.gridLabelToSortableProperty.get(request.sortColumnName as string);
    }

    try {
      const response = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.EPS,
        endpoint: 'ProtectedEntitiesEPS',
        method: 'readRecordsNirvana',
        payload: payload,
      });

      const gridItems = response.data
        .filter((entity: ProtectedEntitiesNetwork) => entity.specifics && Object.keys(entity.specifics).length)
        .map(this.protectedEntityToGridItem);
      this.countPromiseFunctions.resolve(response.total);

      return sanitizeObject({
        rawEvents: gridItems,
      });
    } catch (error) {
      this.error$.next(error);
    }
  }

  shouldDataReloadOnResetView(): boolean {
    this.skipNextLoad = true;
    return true;
  }

  /* This is called both by getSummary and getRows, so for each request, this is called twice. If the flag is set,
   we want to just skip the load for summary, and for rows we also clear the flag; we need to clear it just once.
   If the flag isn't set, we want to setup the promises for getting the count ( number of items in the grid).
  */
  private shouldContinueRequest(shouldClearLoadMutex: boolean): boolean {
    if (this.skipNextLoad) {
      if (shouldClearLoadMutex) {
        this.skipNextLoad = false;
      }
      return false;
    }

    this.setupCountPromise();

    return true;
  }

  private setupCountPromise(): void {
    if (!this.getCountPromise) {
      this.getCountPromise = new Promise((resolve, reject) => {
        this.countPromiseFunctions = { resolve, reject };
      });
    }
  }

  private protectedEntityToGridItem(protectedEntity: ProtectedEntitiesNetwork): NetworkGridItem {
    return {
      _id: protectedEntity._id,
      company: protectedEntity.company?.name,
      name: Translator.translateProtectedEntityNameByType(
        protectedEntity.name,
        protectedEntity.type,
        Constants.ProductTypes.GRAVITY_ZONE_CLOUD
      ),
      fqdn: protectedEntity.specifics.fqdn,
      endpointType: protectedEntity.specifics.isVirtualServer,
      osVersion: protectedEntity.specifics.operatingSystemVersion,
      osType: protectedEntity.specifics.operatingSystem,
      ip: protectedEntity.specifics.ip,
      label: protectedEntity.specifics.label,
      containerId: protectedEntity.specifics.uuid,
      containerName: protectedEntity.specifics.imageName,
      lastSeen: {
        specifics: {
          lastSync: protectedEntity.specifics.lastSync
            ? (protectedEntity.specifics?.lastSync as unknown as string)
            : null,
          isOnline: protectedEntity.specifics.isOnline,
        },
      },
      users: protectedEntity.specifics.endpointUsers,
      type: protectedEntity.type,
      policy: {
        specifics: {
          serverHasNsxIntegration: protectedEntity.specifics.serverHasNsxIntegration,
          clientRules: protectedEntity.specifics.clientRules,
          clientPolicy: protectedEntity.specifics.clientPolicy,
          powerUser: protectedEntity.specifics.isPowerUser,
          inherited:
            !!protectedEntity.activePolicy?.inheritedFrom?.id || !!protectedEntity.assignedPolicy?.inheritedFrom?.id,
          directlyApplied:
            protectedEntity.activePolicy?.inheritedFrom?.id === null ||
            (protectedEntity.activePolicy?.inheritedFrom?.id === undefined &&
              protectedEntity.assignedPolicy?.inheritedFrom?.id === null),
        },
        type: protectedEntity.type,
        assignedPolicy: protectedEntity.assignedPolicy,
        activePolicy: protectedEntity.activePolicy,
        clientPolicy: protectedEntity.specifics.clientPolicy,
      },
      // This is populated by ProtectedEntities::populateGzTagsNames only for readRecords
      tags: protectedEntity.specifics.gzTags,
      dbRecord: protectedEntity,
      pendingActivity: {
        pendingRestart: protectedEntity.specifics.pendingRestart || protectedEntity.specifics.pendingRestartForHvi,
        pendingRestartPatch: protectedEntity.specifics.pendingRestartPatchManagement,
        troubleshootingState: protectedEntity.troubleshooting?.state,
      },
      securityIssues: {
        specifics: {
          isIsolated: protectedEntity.specifics.isIsolated,
          isManaged: protectedEntity.specifics.isManaged,
        },
        hasSecurityIssues: protectedEntity.hasSecurityIssues,
      },
    };
  }

  private transformGridFiltersForBackend(allFilters: Array<Filter<NetworkColumn>>) {
    const [additionalFiltersFromGrid, rawGridFilters] = partition(allFilters, filter =>
      this.keysOfAdditionalFilters.includes(Object.keys(filter)[0] as NetworkColumn)
    );
    const extFilters: ExtJsFilter<NetworkColumn>[] = convertGridFiltersToExtJsFilters(rawGridFilters);

    const gridFilters = extFilters.filter(this.shouldKeepFilterInQuery).map(gridFilter => {
      const filterableProperty = this.gridLabelToFilterableProperty.has(gridFilter.property)
        ? this.gridLabelToFilterableProperty.get(gridFilter.property)
        : gridFilter.property;
      const backendValue = this.transformValueForFilter(gridFilter);
      return {
        property: filterableProperty,
        value: backendValue,
      };
    });

    return { additionalFiltersFromGrid, gridFilters };
  }

  private transformValueForFilter(gridFilter: ExtJsFilter<NetworkColumn>): any {
    switch (gridFilter.property) {
      case NetworkColumn.LAST_SEEN: {
        const lastSyncToNumDays = NavigationHelper.getLastSyncToTimeRange(Constants);
        const lastSeenConstant = gridFilter.value;
        const interval = getAbsoluteTimerangeFromRelativeInterval(
          lastSyncToNumDays.get(lastSeenConstant),
          getTimezone()
        );
        const unixInterval = {
          fromTimestamp: jsTimestampToUnixTimestamp(interval.fromTimestamp as number),
          toTimestamp: jsTimestampToUnixTimestamp(interval.toTimestamp as number),
        };
        return lastSyncToNumDays.has(lastSeenConstant)
          ? { type: lastSeenConstant, interval: unixInterval }
          : lastSeenConstant;
      }
      case NetworkColumn.COMPANY:
        return [gridFilter.value];
      case NetworkColumn.NAME:
      case NetworkColumn.IP:
      case NetworkColumn.OS_VERSION:
      case NetworkColumn.LABEL:
      case NetworkColumn.FQDN:
      case NetworkColumn.CONTAINER_ID:
      case NetworkColumn.CONTAINER_NAME:
        return (gridFilter.value as string).trim();
      default:
        return gridFilter.value;
    }
  }

  private shouldKeepFilterInQuery(gridFilter: ExtJsFilter<NetworkColumn>): any {
    switch (gridFilter.property) {
      case NetworkColumn.LAST_SEEN:
      case NetworkColumn.POLICY:
      case NetworkColumn.COMPANY:
        return !isEmpty(gridFilter.value);
      case NetworkColumn.TAGS:
        return gridFilter.value.length > 0;
      default:
        return true;
    }
  }

  private buildAdditionalFilters(additionalFilterProps: AdditionalFilterProps): AdditionalFilters {
    const additionalFilters: AdditionalFilters = {
      depth: additionalFilterProps.depth,
    };

    AdditionalFiltersHelper.setAdditionalFilters(additionalFilterProps.additionalFiltersFromGrid, additionalFilters);

    return additionalFilters;
  }

  private shouldAddSortToRequest(request: GetRowsRequest<Column, NetworkColumn>): boolean {
    if (!request.sortDirection) {
      return false;
    }

    if (!this.sortDirections.includes(request.sortDirection)) {
      return false;
    }

    if (!request.sortColumnName) {
      return false;
    }

    return this.gridLabelToSortableProperty.has(request.sortColumnName);
  }
}
